import type {AppProps} from 'next/app'
import {useRouter} from 'next/router'
import {useReportWebVitals} from 'next/web-vitals'
import {appWithTranslation, UserConfig} from 'next-i18next'
import React from 'react'
import {TemplatesThemeProvider} from '@miro-site/templates-design'
import {LocalizationProvider} from '@mirohq-internal/website-localization'
import {ServicesProvider} from '@mirohq-internal/website-services-provider'
import {reportPerformanceStat} from '@mirohq-internal/website-web-tracking'
import * as Sentry from '@sentry/node'
import '../src/utils/polyfills'
import '@mirohq-internal/website-ui-foundation/vis-theme'
import nextI18nextConfig from '../next-i18next.config'
import useRouterScroll from '../src/hooks/useRouterScroll'
import '@mirohq-internal/website-ui-foundation/styles/scss'

if (process.env.NEXT_PUBLIC_SENTRY_DSN) {
  Sentry.init({
    enabled: Boolean(Number(process.env.NEXT_PUBLIC_SENTRY_ENABLE)),
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  })
}

const Templates = (props: AppProps): JSX.Element => {
  const {Component, pageProps, err} = props as AppProps & {
    err: unknown
  }

  const locale = pageProps?.['locale'] || 'en-US'
  const translationLinks =
    pageProps['translations']?.map(entry => ({
      locale: entry.locale,
      url: entry.slug,
    })) || []

  const router = useRouter()

  useReportWebVitals(metric => {
    /*
     * Report web vitals only when the router is ready
     * This is to avoid reporting web vitals twice due to rerenders
     * Dynamic pages or apps with rewrites trigger a rerender
     * This is an expected behavior https://nextjs.org/docs/pages/building-your-application/rendering/automatic-static-optimization#how-it-works
     */
    if (router.isReady) {
      reportPerformanceStat(metric, {
        page_locale: router.locale,
        page_slug: pageProps['slug'],
        page_category: `templates-${pageProps['pageType'] || 'unknown'}`,
      })
    }
  })

  useRouterScroll()

  return (
    <ServicesProvider withUserTracking>
      <TemplatesThemeProvider>
        <LocalizationProvider
          detectLocale={false}
          detectTranslations={false}
          data={{currentLocale: locale, translations: translationLinks}}
        >
          <Component {...pageProps} err={err} />
        </LocalizationProvider>
      </TemplatesThemeProvider>
    </ServicesProvider>
  )
}

export default appWithTranslation(Templates, nextI18nextConfig as unknown as UserConfig)
